'use client';

/**
 * DataTable Component
 *
 * A feature-rich data table built with @tanstack/react-table.
 * Supports sorting, filtering, pagination, row selection, and neon theming.
 */

import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import { keyframes, styled, useTheme } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { glassEffects } from '../../theme/shadows';
import { transitionPresets } from '../../theme/transitions';
import { fontSizes } from '../../theme/typography';
import type { DataTableColumn, DataTableProps } from '../../types/dataTable';
import { DataTableHeader } from './DataTableHeader';
import { DataTablePagination } from './DataTablePagination';
import { DataTableToolbar } from './DataTableToolbar';

// ============================================================================
// Animations
// ============================================================================

const shimmerAnimation = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// ============================================================================
// Styled Components
// ============================================================================

interface StyledContainerProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $variant: 'glass' | 'neon' | 'minimal';
}

const StyledContainer = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledContainerProps>(({ $isDark, $neonAccent, $variant }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  const baseStyles = {
    borderRadius: 12,
    overflow: 'hidden',
    transition: transitionPresets.card,
  };

  if ($variant === 'neon' && neonStyles) {
    return {
      ...baseStyles,
      backgroundColor: neonStyles.background,
      border: `1px solid ${neonStyles.border}`,
    };
  }

  if ($variant === 'minimal') {
    return {
      ...baseStyles,
      backgroundColor: 'transparent',
      border: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
    };
  }

  // Glass variant (default)
  return {
    ...baseStyles,
    backgroundColor: glass.background,
    backdropFilter: glass.backdropBlur,
    border: `1px solid ${glass.border}`,
    boxShadow: glass.shadow,
  };
});

interface StyledTableCellProps {
  $isDark: boolean;
  $density: 'compact' | 'normal' | 'comfortable';
  $neonAccent?: NeonAccent;
  $align?: 'left' | 'center' | 'right';
}

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTableCellProps>(({ theme, $isDark, $density, $align = 'left' }) => {
  const paddingMap = {
    compact: theme.spacing(0.5, 1.25),
    normal: theme.spacing(1, 1.5),
    comfortable: theme.spacing(1.5, 2),
  };
  const fontSizeMap = {
    compact: '0.8125rem',
    normal: '0.8125rem',
    comfortable: fontSizes.sm,
  };

  return {
    borderBottom: `1px solid ${$isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.06)'}`,
    padding: paddingMap[$density],
    fontSize: fontSizeMap[$density],
    lineHeight: 1.4,
    color: $isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
    textAlign: $align,
    transition: transitionPresets.colors,
  };
});

interface StyledTableRowProps {
  $isDark: boolean;
  $hoverable: boolean;
  $striped: boolean;
  $isEven: boolean;
  $selected: boolean;
  $neonAccent?: NeonAccent;
  $clickable: boolean;
}

const StyledTableRow = styled(TableRow, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledTableRowProps>(({ $isDark, $hoverable, $striped, $isEven, $selected, $neonAccent, $clickable }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    transition: transitionPresets.colors,
    cursor: $clickable ? 'pointer' : 'default',

    ...($striped &&
      $isEven && {
        backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
      }),

    ...($selected && {
      backgroundColor: neonStyles
        ? neonStyles.background
        : $isDark
          ? 'rgba(99, 91, 255, 0.15)'
          : 'rgba(99, 91, 255, 0.08)',
    }),

    ...($hoverable &&
      !$selected && {
        '&:hover': {
          backgroundColor: $isDark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)',
        },
      }),
  };
});

interface StyledCheckboxProps {
  $neonAccent?: NeonAccent;
  $isDark: boolean;
}

const StyledCheckbox = styled(Checkbox, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCheckboxProps>(({ $neonAccent, $isDark }) => {
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    padding: 4,
    color: $isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.4)',

    '&.Mui-checked': {
      color: neonStyles?.base,
    },

    '&.MuiCheckbox-indeterminate': {
      color: neonStyles?.base,
    },
  };
});

interface ShimmerRowProps {
  $isDark: boolean;
}

const ShimmerCell = styled(Box, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<ShimmerRowProps>(({ $isDark }) => ({
  height: 16,
  borderRadius: 4,
  background: $isDark
    ? 'linear-gradient(90deg, rgba(255,255,255,0.08) 25%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 75%)'
    : 'linear-gradient(90deg, rgba(0,0,0,0.06) 25%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0.06) 75%)',
  backgroundSize: '200% 100%',
  animation: `${shimmerAnimation} 1500ms infinite linear`,
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  textAlign: 'center',
}));

// ============================================================================
// Helper Functions
// ============================================================================

function convertColumns<T>(columns: DataTableColumn<T>[]): ColumnDef<T>[] {
  return columns.map((col) => ({
    id: col.id,
    header: col.headerCell ? col.headerCell : col.header,
    accessorFn: typeof col.accessor === 'function' ? col.accessor : (row: T) => row[col.accessor as keyof T],
    cell: col.cell
      ? ({ getValue, row }) => col.cell!(getValue(), row.original, row.index)
      : ({ getValue }) => {
          const value = getValue();
          return value != null ? String(value) : '';
        },
    enableSorting: col.sortable !== false,
    size: typeof col.width === 'number' ? col.width : undefined,
    minSize: col.minWidth,
    maxSize: col.maxWidth,
    meta: {
      align: col.align,
    },
  }));
}

// ============================================================================
// Component
// ============================================================================

export function DataTable<T>({
  data,
  columns,
  getRowId,
  loading = false,
  error,

  // Sorting
  sortable = true,
  sort,
  onSortChange,

  // Filtering
  filterable = false,
  searchValue: controlledSearchValue,
  onSearchChange,
  // searchableColumns - reserved for future use

  // Pagination
  paginated = true,
  pagination,
  onPaginationChange,

  // Selection
  selectionMode = 'none',
  selectedIds,
  onSelectionChange,

  // Row actions
  onRowClick,
  onRowDoubleClick,

  // Column visibility
  columnVisibility = false,
  // visibleColumns - reserved for future use
  // onColumnVisibilityChange - reserved for future use

  // Appearance
  variant = 'glass',
  neonAccent,
  density = 'normal',
  striped = false,
  hoverable = true,
  stickyHeader = false,
  autoContain: _autoContain,
  height,
  emptyState,
  toolbar,
  footer,

  // Column Resizing — opt-in. When disabled (default), the browser uses
  // natural HTML table layout so columns distribute proportionally across
  // the container width instead of getting forced 150px pixel widths.
  columnResizing = false,
  onColumnSizingChange: onColumnSizingChangeProp,

  // Server-side
  serverSide = false,
  totalRows: serverTotalRows,
}: DataTableProps<T>): React.ReactElement {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useI18n();

  // Height: only use explicit height prop when provided. No auto-containment —
  // tables render at natural height and the parent scroll container handles overflow.
  const effectiveHeight = height || undefined;
  const effectiveStickyHeader = stickyHeader;

  // Internal state
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const [sorting, setSorting] = useState<SortingState>(() => {
    if (sort) {
      return [{ id: sort.column, desc: sort.direction === 'desc' }];
    }
    return [];
  });
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>(() => {
    if (selectedIds) {
      const selection: Record<string, boolean> = {};
      selectedIds.forEach((id) => {
        selection[id] = true;
      });
      return selection;
    }
    return {};
  });
  const [internalPagination, setInternalPagination] = useState({
    pageIndex: pagination?.page ?? 0,
    pageSize: pagination?.pageSize ?? 10,
  });
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});

  // Use controlled or internal search value
  const searchValue = controlledSearchValue ?? internalSearchValue;
  const handleSearchChange = onSearchChange ?? setInternalSearchValue;

  // Convert columns to tanstack format
  const tableColumns = useMemo(() => {
    const cols = convertColumns(columns);

    // Add selection column if needed
    if (selectionMode !== 'none') {
      const selectionColumn: ColumnDef<T> = {
        id: '_selection',
        header: ({ table }) =>
          selectionMode === 'multiple' ? (
            <StyledCheckbox
              $neonAccent={neonAccent}
              $isDark={isDark}
              checked={table.getIsAllRowsSelected()}
              indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
              size="small"
            />
          ) : null,
        cell: ({ row }) => (
          <StyledCheckbox
            $neonAccent={neonAccent}
            $isDark={isDark}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
            size="small"
          />
        ),
        size: 48,
        enableSorting: false,
      };
      return [selectionColumn, ...cols];
    }

    return cols;
  }, [columns, selectionMode, neonAccent, isDark]);

  // Handle selection changes
  const handleRowSelectionChange = useCallback(
    (updater: ((old: Record<string, boolean>) => Record<string, boolean>) | Record<string, boolean>) => {
      const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(newSelection);

      if (onSelectionChange) {
        const selectedRowIds = new Set(Object.keys(newSelection).filter((id) => newSelection[id]));
        const selectedRowData = data.filter((row) => selectedRowIds.has(getRowId(row)));
        onSelectionChange(selectedRowIds, selectedRowData);
      }
    },
    [rowSelection, onSelectionChange, data, getRowId],
  );

  // Create table instance
  const table = useReactTable({
    data,
    columns: tableColumns,
    enableColumnResizing: columnResizing,
    columnResizeMode: 'onChange' as const,
    columnResizeDirection: 'ltr' as const,
    state: {
      sorting,
      globalFilter: searchValue,
      rowSelection,
      pagination: internalPagination,
      columnSizing,
    },
    getRowId,
    onColumnSizingChange: (updater) => {
      const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
      setColumnSizing(newSizing);
      if (onColumnSizingChangeProp) {
        onColumnSizingChangeProp(newSizing);
      }
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);

      if (onSortChange && newSorting.length > 0) {
        onSortChange({
          column: newSorting[0].id,
          direction: newSorting[0].desc ? 'desc' : 'asc',
        });
      } else if (onSortChange && newSorting.length === 0) {
        onSortChange(undefined);
      }
    },
    onGlobalFilterChange: handleSearchChange,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(internalPagination) : updater;
      setInternalPagination(newPagination);

      if (onPaginationChange) {
        onPaginationChange({
          page: newPagination.pageIndex,
          pageSize: newPagination.pageSize,
          pageSizeOptions: pagination?.pageSizeOptions,
        });
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: sortable ? getSortedRowModel() : undefined,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: paginated && !serverSide ? getPaginationRowModel() : undefined,
    enableRowSelection: selectionMode !== 'none',
    enableMultiRowSelection: selectionMode === 'multiple',
    manualPagination: serverSide,
    pageCount: serverSide && serverTotalRows ? Math.ceil(serverTotalRows / internalPagination.pageSize) : undefined,
  });

  const rows = table.getRowModel().rows;
  const totalRowCount = serverSide ? (serverTotalRows ?? 0) : table.getFilteredRowModel().rows.length;

  // Render loading state
  const renderLoadingRows = () => {
    const loadingRowCount = internalPagination.pageSize;
    return Array.from({ length: loadingRowCount }).map((_, rowIndex) => (
      <TableRow key={`loading-${rowIndex}`}>
        {tableColumns.map((_, colIndex) => (
          <StyledTableCell
            key={`loading-${rowIndex}-${colIndex}`}
            $isDark={isDark}
            $density={density}
            $neonAccent={neonAccent}
          >
            <ShimmerCell $isDark={isDark} sx={{ width: `${60 + Math.random() * 30}%` }} />
          </StyledTableCell>
        ))}
      </TableRow>
    ));
  };

  // Render empty state
  const renderEmptyState = () => {
    if (emptyState) {
      return (
        <TableRow>
          <TableCell colSpan={tableColumns.length}>{emptyState}</TableCell>
        </TableRow>
      );
    }

    return (
      <TableRow>
        <TableCell colSpan={tableColumns.length}>
          <EmptyStateContainer>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                color: 'text.secondary',
              }}
            >
              {t('common.noData')}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.disabled',
              }}
            >
              {searchValue ? t('common.adjustSearchOrFilters') : t('common.addDataToStart')}
            </Typography>
          </EmptyStateContainer>
        </TableCell>
      </TableRow>
    );
  };

  // Render error state
  if (error) {
    return (
      <StyledContainer $isDark={isDark} $neonAccent={neonAccent} $variant={variant}>
        <EmptyStateContainer>
          <Typography variant="body2" color="error" gutterBottom>
            {t('common.errorLoadingData')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            {error}
          </Typography>
        </EmptyStateContainer>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer $isDark={isDark} $neonAccent={neonAccent} $variant={variant}>
      {/* Toolbar */}
      {(filterable || toolbar) && (
        <DataTableToolbar
          searchValue={searchValue}
          onSearchChange={filterable ? handleSearchChange : undefined}
          searchPlaceholder={t('common.search')}
          onFilterClick={filterable ? () => {} : undefined}
          onColumnVisibilityClick={columnVisibility ? () => {} : undefined}
          actions={toolbar}
          neonAccent={neonAccent}
        />
      )}

      {/* Table */}
      <TableContainer
        sx={{
          maxHeight: effectiveHeight,
          overflowX: 'auto',
        }}
      >
        <Table
          stickyHeader={effectiveStickyHeader}
          size={density === 'compact' ? 'small' : 'medium'}
          style={{
            width: '100%',
            ...(columnResizing && { minWidth: table.getCenterTotalSize() }),
          }}
        >
          <DataTableHeader
            headerGroups={table.getHeaderGroups()}
            sortable={sortable}
            neonAccent={neonAccent}
            columnResizing={columnResizing}
          />
          <TableBody>
            {loading
              ? renderLoadingRows()
              : rows.length === 0
                ? renderEmptyState()
                : rows.map((row, index) => {
                    const isSelected = row.getIsSelected();

                    return (
                      <StyledTableRow
                        key={row.id}
                        $isDark={isDark}
                        $hoverable={hoverable}
                        $striped={striped}
                        $isEven={index % 2 === 0}
                        $selected={isSelected}
                        $neonAccent={neonAccent}
                        $clickable={!!onRowClick || !!onRowDoubleClick}
                        onClick={() => onRowClick?.(row.original)}
                        onDoubleClick={() => onRowDoubleClick?.(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const align = (
                            cell.column.columnDef.meta as { align?: 'left' | 'center' | 'right' } | undefined
                          )?.align;
                          const hasExplicitSize = cell.column.columnDef.size !== undefined;
                          const widthStyle =
                            columnResizing || hasExplicitSize ? { width: cell.column.getSize() } : undefined;

                          return (
                            <StyledTableCell
                              key={cell.id}
                              $isDark={isDark}
                              $density={density}
                              $neonAccent={neonAccent}
                              $align={align}
                              style={widthStyle}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                    );
                  })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {paginated && (
        <DataTablePagination
          page={internalPagination.pageIndex}
          pageSize={internalPagination.pageSize}
          totalRows={totalRowCount}
          pageSizeOptions={pagination?.pageSizeOptions ?? [10, 25, 50, 100]}
          onPageChange={(page) => table.setPageIndex(page)}
          onPageSizeChange={(size) => table.setPageSize(size)}
          neonAccent={neonAccent}
        />
      )}

      {/* Custom footer */}
      {footer}
    </StyledContainer>
  );
}

export default DataTable;
